import { isArray } from 'radash';
import * as React from 'react';

import { useLayout } from '@/components/datamodel/hooks/useLayout';
import { getFilter } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';

// Icon mapping for different hierarchy types
const HIERARCHY_ICON_MAP: Record<string, React.ComponentType<any>> = {
    'PackagingUnitBase': () => null, // Placeholder - will be imported in the component
    'PackagingUnitCase': () => null,
    'PackagingUnitDisplay': () => null,
    'PackagingUnitPallet': () => null,
    'PackagingUnitContainer': () => null,
    'packaging': () => null
};

interface UseHierarchyElementsFilterProps {
    hierarchyNodes: any[];
    allItemsData: any[];
    hierarchiesData: any[];
    isAllItemsDataLoading: boolean;
    hierarchyIconMap?: Record<string, React.ComponentType<any>>;
}

export const useHierarchyElementsFilter = ({
    hierarchyNodes,
    allItemsData,
    hierarchiesData,
    isAllItemsDataLoading,
    hierarchyIconMap = HIERARCHY_ICON_MAP
}: UseHierarchyElementsFilterProps) => {
    const { layout } = useLayout();

    // Get the new hierarchy elements filter
    let customHierarchyElementFilter: ReturnType<typeof getFilter> | undefined;

    if (!layout?.layoutOptions?.hierarchyNavigation?.hierarchy_elements_filter) {
        logger.warn('Editor layout has no hierarchy elements filter defined.');
    } else {
        customHierarchyElementFilter = getFilter(layout.layoutOptions.hierarchyNavigation.hierarchy_elements_filter);
        if (!customHierarchyElementFilter) {
            logger.error(`Custom filter with name ${layout.layoutOptions.hierarchyNavigation.hierarchy_elements_filter} was not found.`);
        }
    }

    // Apply the new hierarchy filter to get enhanced hierarchy data with titleOne, titleTwo, and icon
    const enhancedHierarchyNodes = React.useMemo(() => {
        if (!isAllItemsDataLoading) {
            try {
                // Hierarchy nodes whose items were readable; unreadable items are not retrived from the backend
                const readableHierarchyNodes = hierarchyNodes?.filter(node => {
                    const itemData = allItemsData?.find(item => item.primaryKey__ === node.itemPrimaryKey);
                    return itemData;
                });

                // Create enhanced elements with hierarchy data and item data
                const filteredElements = customHierarchyElementFilter && customHierarchyElementFilter(readableHierarchyNodes, allItemsData, hierarchyIconMap);
                const enhancedElements = filteredElements?.map((node:any) => {
                    const hierarchyElement = (isArray(hierarchiesData))? hierarchiesData?.find(el => el.primaryKey === node.itemPrimaryKey): null;
                    const filteredHierarchyElement = readableHierarchyNodes?.find((el: any) => node.primaryKey === el?.itemPrimaryKey);
                    const itemData = allItemsData?.find(item => item.primaryKey__ === node.primaryKey);

                    return {
                        ...node,
                        hierarchyElement,
                        itemData,
                        ...filteredHierarchyElement
                    };
                });

                return enhancedElements;
            } catch (error) {
                logger.error('Error applying hierarchy filter:', error);
                return hierarchyNodes;
            }
        }
        return hierarchyNodes;
    }, [isAllItemsDataLoading, hierarchyNodes, allItemsData, hierarchiesData, customHierarchyElementFilter, hierarchyIconMap]);

    return { enhancedHierarchyNodes };
};
